import React, { useEffect, useMemo, useState } from 'react';
import {
  Users, ShieldCheck, UserCog, Building2, UserPlus, Download,
  Edit, Trash2, ChevronRight, ChevronDown, RotateCw
} from 'lucide-react';
import {
  PageHead, Panel, StatCard, StatRow, Pill, Table, Row, Cell, Pagination, Avatar, btn, field, Select,
} from '../common/AdminUI';
import { buildCsv } from '../../utils/exportCsv';

const PAGE_SIZE = 10;

const ROLE_PILL = { super_admin: 'violet', admin: 'blue', employee: 'cyan', accountant: 'amber', user: 'slate' };
const ROLE_TEXT = { super_admin: 'Super Admin', admin: 'Admin', employee: 'Employee', accountant: 'Accountant', user: 'User' };

const displayRole = (role) => (role === 'customer' ? 'user' : role);
const phoneOf = (u) => {
  const digits = String(u.mobile || u.phoneNumber || u.phNum || '').replace(/\D/g, '');
  if (!digits) return '—';
  return `+91 ${digits.slice(-10)}`;
};
const nameOf = (u) => u.name || u.customerName || u.our_users || u.username || '—';

// A shut row should still say what is under it, so the team is counted by role
// and shown as chips rather than a bare "owns 7 accounts".
const CHIP_TONES = {
  employee:   'bg-cyan-50 text-cyan-700 ring-cyan-200/70 dark:bg-cyan-500/10 dark:text-cyan-300 dark:ring-cyan-500/20',
  accountant: 'bg-amber-50 text-amber-700 ring-amber-200/70 dark:bg-amber-500/10 dark:text-amber-300 dark:ring-amber-500/20',
  user:       'bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-700/50 dark:text-slate-300 dark:ring-slate-600/50',
};
const CHIP_TEXT = { employee: ['emp', 'emp'], accountant: ['acct', 'acct'], user: ['user', 'users'] };

const breakdownOf = (children) => ['employee', 'accountant', 'user']
  .map((role) => ({ role, n: children.filter((c) => displayRole(c.role) === role).length }))
  .filter((b) => b.n > 0)
  .map((b) => ({ ...b, label: CHIP_TEXT[b.role][b.n === 1 ? 0 : 1] }));

/**
 * Users Management for the admin consoles.
 * Admins carry their own people underneath them, so a row opens to reveal them;
 * in an admin's own console nobody has children, and the same rows read flat.
 */
const SuperAdminUsers = ({
  users = [], currentUser, loading, error,
  onAdd, onEdit, onDelete, onRetry,
  search = '', scope = 'super_admin',
}) => {
  const isSuper = scope === 'super_admin';
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [localSearch, setLocalSearch] = useState('');
  const controlled = typeof search === 'string';
  const [expanded, setExpanded] = useState(() => new Set());
  const [page, setPage] = useState(1);

  const term = (controlled ? search : localSearch).trim().toLowerCase();

  // Searching from the topbar skips the box that used to reset the pager, so
  // a narrowed list could land you on a page that no longer exists.
  useEffect(() => { setPage(1); }, [term]);

  const counts = useMemo(() => ({
    total: users.length,
    superAdmins: users.filter((u) => displayRole(u.role) === 'super_admin').length,
    admins: users.filter((u) => displayRole(u.role) === 'admin').length,
    staff: users.filter((u) => ['employee', 'accountant'].includes(displayRole(u.role))).length,
    employees: users.filter((u) => displayRole(u.role) === 'employee').length,
    accountants: users.filter((u) => displayRole(u.role) === 'accountant').length,
    clients: users.filter((u) => displayRole(u.role) === 'user').length,
    active: users.filter((u) => (u.status || 'active') === 'active').length,
  }), [users]);

  // Admins own the people they created; everyone else stands on their own.
  const groups = useMemo(() => {
    const roleOrder = { employee: 0, accountant: 1, user: 2 };
    const admins = users.filter((u) => displayRole(u.role) === 'admin').sort((a, b) => b.id - a.id);
    const ownedIds = new Set();
    const withChildren = admins.map((admin) => {
      const children = users
        .filter((u) => u.created_by === admin.id && displayRole(u.role) !== 'admin')
        .sort((a, b) => {
          const ra = roleOrder[displayRole(a.role)] ?? 3;
          const rb = roleOrder[displayRole(b.role)] ?? 3;
          return ra !== rb ? ra - rb : b.id - a.id;
        });
      children.forEach((c) => ownedIds.add(c.id));
      return { head: admin, children };
    });
    const loose = users
      .filter((u) => displayRole(u.role) !== 'admin' && !ownedIds.has(u.id))
      .sort((a, b) => b.id - a.id)
      .map((u) => ({ head: u, children: [] }));
    return [...withChildren, ...loose];
  }, [users]);

  const matches = (u) => {
    if (!u) return false;
    const role = displayRole(u.role);
    if (roleFilter !== 'all' && role !== roleFilter) return false;
    if (statusFilter !== 'all' && (u.status || 'active') !== statusFilter) return false;
    if (!term) return true;
    return [nameOf(u), u.email, u.username, phoneOf(u), String(u.id)]
      .some((v) => String(v || '').toLowerCase().includes(term));
  };

  const visible = useMemo(() => groups.reduce((out, g) => {
    if (matches(g.head)) out.push(g);
    else {
      const children = g.children.filter(matches);
      if (children.length) out.push({ ...g, children, forceOpen: true });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []), [groups, term, roleFilter, statusFilter]);

  const pageCount = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
  const current = Math.min(page, pageCount);
  const rows = visible.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  // Opening eight branches one at a time is the tedious way to read the tree.
  const withTeams = visible.filter((g) => g.children.length > 0).map((g) => g.head.id);
  const allOpen = withTeams.length > 0 && withTeams.every((id) => expanded.has(id));

  const toggle = (id) => setExpanded((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const exportUsers = () => {
    const headers = ['ID', 'Name', 'Email', 'Phone', 'Username', 'Role', 'Status', 'Created By'];
    const body = users.map((u) => [
      u.id, nameOf(u), u.email || '', phoneOf(u), u.username || '',
      ROLE_TEXT[displayRole(u.role)] || u.role, u.status || 'active', u.created_by ?? '',
    ]);
    const blob = new Blob([buildCsv(headers, body)], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `users_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  // The ID cell carries the tree: a spine down the open group and an elbow into
  // each person, so the team reads as one branch instead of eight loose rows.
  // Cell padding moves onto the inner div so the spine spans the whole row and
  // the segments meet across the divider.
  const personRow = (u, {
    child = false, hasChildren = false, open = false, last = false, locked = false, onToggle,
  } = {}) => {
    // While a search pins a branch open, clicking it cannot shut it — so it
    // does not pretend to be clickable either.
    const clickable = hasChildren && !locked;
    const role = displayRole(u.role);
    const tone = ROLE_PILL[role] || 'slate';
    const children = hasChildren ? (groups.find((g) => g.head.id === u.id)?.children || []) : [];
    const chips = breakdownOf(children);

    return (
      <Row
        key={`${child ? 'c' : 'p'}-${u.id}`}
        onClick={clickable ? onToggle : undefined}
        title={clickable ? (open ? 'Hide this team' : 'Show this team') : undefined}
        className={`${child
          ? 'bg-slate-50/60 dark:bg-slate-800/25'
          : open ? 'bg-blue-50/40 dark:bg-blue-500/[0.06]' : ''} ${clickable ? 'cursor-pointer' : ''}`}
      >
        <Cell className="whitespace-nowrap relative">
          {child && (
            <>
              <span className={`absolute w-0.5 bg-slate-300 dark:bg-slate-600 left-[27px] sm:left-[31px]
                                ${last ? 'top-0 h-1/2' : 'inset-y-0'}`} />
              <span className="absolute h-0.5 rounded-full bg-slate-300 dark:bg-slate-600
                               left-[27px] sm:left-[31px] w-[15px] top-1/2 -translate-y-1/2" />
            </>
          )}
          {!child && open && hasChildren && (
            <span className="absolute w-0.5 bg-slate-300 dark:bg-slate-600 left-[27px] sm:left-[31px] bottom-0"
                  style={{ top: 'calc(50% + 11px)' }} />
          )}
          <div className="relative flex items-center gap-2" style={{ paddingLeft: child ? 30 : 0 }}>
            {hasChildren ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); if (!locked) onToggle?.(); }}
                title={open ? 'Hide this team' : `Show ${children.length} team accounts`}
                className={`grid place-items-center w-6 h-6 rounded-lg border transition flex-shrink-0
                  ${open
                    ? 'border-blue-300 bg-blue-600 text-white dark:border-blue-500/50'
                    : 'border-slate-200 bg-white text-slate-400 hover:border-blue-300 hover:text-blue-600 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:text-blue-300'}`}
              >
                {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
              </button>
            ) : <span className="w-6 flex-shrink-0" />}

            <span className="font-mono text-[12px] text-slate-500 dark:text-slate-400">#{u.id}</span>
          </div>
        </Cell>

        <Cell>
          <div className="flex items-center gap-2.5 min-w-0">
            <Avatar name={nameOf(u)} tone={tone} />
            <div className="min-w-0">
              <p className="font-bold text-slate-900 dark:text-white truncate">{nameOf(u)}</p>
              {chips.length > 0 && (
                <div className="flex items-center gap-1 mt-1 whitespace-nowrap">
                  {chips.map((b) => (
                    <span
                      key={b.role}
                      className={`px-1.5 py-px rounded-md text-[10px] font-bold ring-1 ${CHIP_TONES[b.role]}`}
                    >
                      {b.n} {b.label}
                    </span>
                  ))}
                </div>
              )}
              {child && (
                <p className="text-[11px] text-slate-400 dark:text-slate-500">
                  under {nameOf(users.find((x) => x.id === u.created_by) || {})}
                </p>
              )}
            </div>
          </div>
        </Cell>

        <Cell className="whitespace-nowrap">{u.email || '—'}</Cell>
        <Cell className="whitespace-nowrap">{phoneOf(u)}</Cell>
        <Cell className="whitespace-nowrap font-mono text-[12px]">{u.username || '—'}</Cell>
        <Cell><Pill tone={tone}>{ROLE_TEXT[role] || u.role}</Pill></Cell>
        <Cell>
          <Pill tone={(u.status || 'active') === 'active' ? 'emerald' : 'rose'} dot>
            {(u.status || 'active') === 'active' ? 'Active' : 'Inactive'}
          </Pill>
        </Cell>
        <Cell>
          <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
            <button type="button" className={btn.icon} title="Edit user" onClick={() => onEdit?.(u)}>
              <Edit size={15} />
            </button>
            {currentUser?.id !== u.id && (
              <button type="button" className={btn.iconDanger} title="Delete user" onClick={() => onDelete?.(u)}>
                <Trash2 size={15} />
              </button>
            )}
          </div>
        </Cell>
      </Row>
    );
  };

  return (
    <div>
      <PageHead
        eyebrow={isSuper ? 'Super admin core · Directory' : 'Admin · Directory'}
        title="Users Management"
        badge={<Pill tone="blue">{counts.total} accounts</Pill>}
        subtitle={isSuper
          ? 'System administrators, the ops team and user accounts across every export lane.'
          : 'The ops team and the user accounts you have created.'}
        actions={(
          <>
            <button type="button" className={btn.quiet} onClick={exportUsers}>
              <Download size={16} /> Export Users
            </button>
            <button type="button" className={btn.primary} onClick={onAdd}>
              <UserPlus size={16} /> Add User
            </button>
          </>
        )}
      />

      <StatRow>
        <StatCard label="Total registered" value={counts.total} icon={Users} tone="blue"
          note={`${counts.active} active`} />
        {isSuper ? (
          <StatCard label="Super admins" value={counts.superAdmins} icon={ShieldCheck} tone="violet"
            note="Unrestricted cluster access" />
        ) : (
          <StatCard label="Employees" value={counts.employees} icon={UserCog} tone="violet"
            note="Data entry and operations" />
        )}
        {isSuper ? (
          <StatCard label="Ops team" value={counts.admins + counts.staff} icon={UserCog} tone="emerald"
            note={`${counts.admins} admins · ${counts.staff} employees & accountants`} />
        ) : (
          <StatCard label="Accountants" value={counts.accountants} icon={ShieldCheck} tone="emerald"
            note="Billing and invoices" />
        )}
        <StatCard label="User accounts" value={counts.clients} icon={Building2} tone="amber"
          note="Shippers and forwarders" />
      </StatRow>

      {error && (
        <Panel className="px-5 py-3.5 mb-4 border-rose-200 dark:border-rose-500/30">
          <p className="text-sm text-rose-600 dark:text-rose-400 flex items-center gap-3">
            {error}
            <button type="button" onClick={onRetry} className="inline-flex items-center gap-1.5 font-bold underline">
              <RotateCw size={14} /> Retry
            </button>
          </p>
        </Panel>
      )}

      <Panel className="overflow-hidden">
        <div className="flex flex-col lg:flex-row lg:items-center gap-3 px-4 sm:px-5 py-4 border-b border-slate-100 dark:border-slate-800">
          {!controlled && (
            <input
              value={localSearch}
              onChange={(e) => { setLocalSearch(e.target.value); setPage(1); }}
              placeholder="Search by name, email, phone or user ID…"
              className={`${field} lg:max-w-md`}
            />
          )}
          <Select
            value={roleFilter === 'all' ? '' : roleFilter}
            onChange={(v) => { setRoleFilter(v || 'all'); setPage(1); }}
            allLabel="Role: All roles"
            icon={UserCog}
            className="lg:w-56"
            options={[
              ...(isSuper ? [{ value: 'super_admin', label: 'Super Admins' }, { value: 'admin', label: 'Admins' }] : []),
              { value: 'employee', label: 'Employees' },
              { value: 'accountant', label: 'Accountants' },
              { value: 'user', label: 'Users' },
            ]}
          />
          <Select
            value={statusFilter === 'all' ? '' : statusFilter}
            onChange={(v) => { setStatusFilter(v || 'all'); setPage(1); }}
            allLabel="Status: All"
            className="lg:w-48"
            options={[
              { value: 'active', label: 'Active' },
              { value: 'inactive', label: 'Inactive' },
            ]}
          />
          <div className="flex items-center gap-2 lg:ml-auto">
            {withTeams.length > 0 && (
              <button
                type="button"
                onClick={() => setExpanded(allOpen ? new Set() : new Set(withTeams))}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-xl text-[12.5px] font-bold transition
                           border border-slate-200 bg-white text-slate-600 hover:bg-slate-50
                           dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-300 dark:hover:bg-slate-700/60"
              >
                {allOpen ? <ChevronRight size={14} /> : <ChevronDown size={14} />}
                {allOpen ? 'Collapse all' : 'Expand all'}
              </button>
            )}
            <Pill tone="slate">{visible.length} shown</Pill>
          </div>
        </div>

        {loading ? (
          <div className="py-20 grid place-items-center">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
          </div>
        ) : (
          <>
            <Table
              head={[
                { label: 'ID' }, { label: 'Name' }, { label: 'Email' }, { label: 'Phone' },
                { label: 'Username' }, { label: 'Role' }, { label: 'Status' }, { label: 'Actions' },
              ]}
              empty="No account matches these filters"
            >
              {rows.flatMap((g) => {
                const open = g.forceOpen || expanded.has(g.head.id);
                const head = personRow(g.head, {
                  hasChildren: g.children.length > 0,
                  open,
                  locked: !!g.forceOpen,
                  onToggle: () => toggle(g.head.id),
                });
                if (!open) return [head];
                return [head, ...g.children.map((c, i) => personRow(c, {
                  child: true,
                  last: i === g.children.length - 1,
                }))];
              })}
            </Table>
            <Pagination
              page={current}
              pageCount={pageCount}
              onPage={setPage}
              summary={`Showing ${visible.length === 0 ? 0 : (current - 1) * PAGE_SIZE + 1}–${Math.min(current * PAGE_SIZE, visible.length)} of ${visible.length} registered tenants`}
            />
          </>
        )}
      </Panel>
    </div>
  );
};

export default SuperAdminUsers;
